import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { PdfButtons } from "@/components/DiaryPrint";
import { ChooseDocDialog, DOC_TYPES, KINDS, MeetingRecorderButton, StudentPicker, waitStatus } from "@/components/MeetingRecorder";
import { ArrowLeft, Loader2, RefreshCw, Save, Trash2, Wand2 } from "lucide-react";

const STATUS = {
  GRAVANDO: ["Enviando", "bg-slate-500"], TRANSCREVENDO: ["Transcrevendo", "bg-amber-500"], TRANSCRITO: ["Transcrito", "bg-blue-600"],
  GERANDO: ["Gerando", "bg-amber-500"], PRONTO: ["Pronto", "bg-green-600"], ERRO: ["Erro", "bg-red-600"], ERRO_DOC: ["Erro", "bg-red-600"],
};
const fmtDate = (d) => (d ? new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—");

export const MeetingList = ({ studentId, compact }) => {
  const { scopeParams, selectedSchoolId } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/meetings", { params: { ...scopeParams, ...(studentId ? { student_id: studentId } : {}) } }).then((r) => setRows(r.data)).catch(() => setRows([]));
    // eslint-disable-next-line
  }, [selectedSchoolId, studentId]);
  if (rows === null) return <Loader2 className="mx-auto h-5 w-5 animate-spin text-blue-700" />;
  if (!rows.length) return <p className="py-6 text-center text-sm text-slate-400" data-testid="meetings-empty">Nenhuma reunião ou atendimento registrado.</p>;
  return (
    <div className="divide-y divide-slate-100" data-testid="meetings-list">
      {rows.map((m) => {
        const [label, cls] = STATUS[m.status] || [m.status, "bg-slate-500"];
        return (
          <button key={m.id} type="button" onClick={() => navigate(`/reunioes/${m.id}`)} className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-1 py-3 text-left hover:bg-slate-50" data-testid={`meeting-row-${m.id}`}>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-slate-800">{m.title || `${KINDS[m.kind]} sem assunto`}</span>
              <span className="text-xs text-slate-500">{fmtDate(m.created_at)} · {KINDS[m.kind]}{m.doc_type ? ` · ${DOC_TYPES[m.doc_type]}` : ""}{!compact && m.student_name ? ` · ${m.student_name}` : ""} · {m.created_by_name}</span>
            </span>
            <Badge className={cls}>{label}</Badge>
          </button>
        );
      })}
    </div>
  );
};

export default function Reunioes() {
  const { id } = useParams();
  const { scopeParams, isSuperadmin, selectedSchoolId } = useAuth();
  if (id) return <MeetingDetail id={id} />;
  return (
    <div>
      <PageHeader title="Reuniões e Atendimentos" subtitle="Gravações transcritas por IA em Ata, Relatório ou Plano de Ação" />
      <div className="mb-4 flex justify-end">{!(isSuperadmin && !selectedSchoolId) && <MeetingRecorderButton scopeParams={scopeParams} />}</div>
      <Card className="border-slate-200 p-4"><MeetingList /></Card>
    </div>
  );
}

const AudioSeg = ({ id, index }) => {
  const [url, setUrl] = useState(null);
  const load = async () => {
    try { const { data } = await api.get(`/meetings/${id}/audio/${index}`, { responseType: "blob", timeout: 120000 }); setUrl(URL.createObjectURL(data)); }
    catch { toast.error("Não foi possível carregar o áudio"); }
  };
  return url ? <audio controls src={url} className="h-9 w-full" data-testid={`meeting-audio-${index}`} /> : (
    <Button size="sm" variant="outline" onClick={load} data-testid={`meeting-audio-load-${index}`}>Ouvir parte {index + 1}</Button>
  );
};

function MeetingDetail({ id }) {
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [choose, setChoose] = useState(false);
  const [showTr, setShowTr] = useState(false);
  const load = useCallback(() => api.get(`/meetings/${id}`).then((r) => {
    setM(r.data);
    setForm({ title: r.data.title || "", content: r.data.content || "", student: r.data.student_id ? { id: r.data.student_id, full_name: r.data.student_name } : null });
    return r.data;
  }).catch(() => setM(false)), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!m || !["TRANSCREVENDO", "GERANDO", "GRAVANDO"].includes(m.status)) return undefined;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [m, load]);
  if (m === false) return <div className="py-16 text-center text-slate-500">Registro não encontrado.</div>;
  if (!m || !form) return <div className="grid h-64 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-blue-700" /></div>;

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/meetings/${id}`, { title: form.title, content: form.content, student_id: form.student?.id || null, clear_student: !form.student });
      toast.success("Documento salvo"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const retry = async () => {
    try { await api.post(`/meetings/${id}/transcribe`); await waitStatus(id, ["TRANSCRITO"]); const d = await load(); if (d) setChoose(true); }
    catch (e) { toast.error(e.response ? formatApiError(e.response.data?.detail) : e.message); load(); }
  };
  const remove = async () => {
    if (!window.confirm("Excluir este registro e o áudio?")) return;
    await api.delete(`/meetings/${id}`); toast.success("Registro excluído"); navigate("/reunioes");
  };
  const [label, cls] = STATUS[m.status] || [m.status, "bg-slate-500"];
  const working = ["TRANSCREVENDO", "GERANDO", "GRAVANDO"].includes(m.status);

  return (
    <div>
      <Button variant="ghost" size="sm" onClick={() => navigate("/reunioes")} className="mb-3 -ml-2 text-slate-500"><ArrowLeft className="mr-1 h-4 w-4" /> Reuniões e Atendimentos</Button>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">{m.doc_type ? DOC_TYPES[m.doc_type] : "Gravação"}{m.doc_type === "PLANO_REFORCO" ? "" : ` — ${KINDS[m.kind]}`}</h1>
          <p className="text-sm text-slate-500">{fmtDate(m.created_at)} · gravado por {m.created_by_name} <Badge className={`ml-1 ${cls}`} data-testid="meeting-status">{label}</Badge></p>
        </div>
        <div className="flex flex-wrap gap-2">
          {m.transcript && <Button variant="outline" onClick={() => setChoose(true)} disabled={working} data-testid="meeting-choose-btn"><Wand2 className="mr-1.5 h-4 w-4" />{m.content ? "Gerar outro modelo" : "Escolher documento"}</Button>}
          {m.content && <PdfButtons path={`/meetings/${id}/pdf`} params={{}} filename={`${(m.doc_type || "documento").toLowerCase()}_${m.created_at.slice(0, 10)}.pdf`} testid="meeting-pdf" label="Baixar PDF" />}
          <Button variant="ghost" onClick={remove} className="text-red-600" data-testid="meeting-delete-btn"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      {working && <Card className="mb-4 flex items-center gap-3 border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="meeting-working"><Loader2 className="h-4 w-4 animate-spin" />{m.status === "GERANDO" ? "A IA está redigindo o documento..." : "Transcrevendo a gravação..."}</Card>}
      {m.error && <Card className="mb-4 flex flex-wrap items-center justify-between gap-2 border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="meeting-error">{m.error}
        {m.status === "ERRO" && <Button size="sm" variant="outline" onClick={retry} data-testid="meeting-retry-btn"><RefreshCw className="mr-1.5 h-4 w-4" />Tentar novamente</Button>}</Card>}
      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <Card className="border-slate-200 p-4">
          <div className="mb-3 grid gap-3 sm:grid-cols-2">
            <div><Label>Assunto</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="meeting-edit-title" /></div>
            <div><Label>Estudante (opcional)</Label><StudentPicker value={form.student} onChange={(s) => setForm((f) => ({ ...f, student: s }))} /></div>
          </div>
          <Label>Texto do documento</Label>
          <Textarea value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} rows={22} className="font-mono text-sm leading-relaxed"
            placeholder={m.transcript ? "Escolha o modelo de documento para a IA redigir, ou escreva aqui." : "Aguardando a transcrição..."} data-testid="meeting-edit-content" />
          <p className="mt-1 text-xs text-slate-400">Linhas com "## " viram títulos e "- " viram itens no PDF.</p>
          <div className="mt-3 flex justify-end"><Button onClick={save} disabled={busy || working} className="bg-blue-700 hover:bg-blue-800" data-testid="meeting-save-btn">{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}Salvar</Button></div>
        </Card>
        <div className="space-y-4">
          {m.segments.length > 0 && <Card className="border-slate-200 p-4">
            <h3 className="mb-2 text-sm font-bold text-slate-700">Áudio da gravação</h3>
            <div className="space-y-2">{m.segments.map((s) => <AudioSeg key={s.index} id={id} index={s.index} />)}</div>
          </Card>}
          {m.transcript && (
            <Card className="border-slate-200 p-4">
              <button type="button" onClick={() => setShowTr((v) => !v)} className="text-sm font-bold text-blue-700" data-testid="meeting-transcript-toggle">{showTr ? "Ocultar" : "Ver"} transcrição</button>
              {showTr && <p className="mt-2 max-h-80 overflow-y-auto whitespace-pre-wrap text-xs text-slate-600" data-testid="meeting-transcript">{m.transcript}</p>}
            </Card>
          )}
          <Card className="border-slate-200 p-4">
            <h3 className="mb-2 text-sm font-bold text-slate-700">Histórico</h3>
            <ul className="space-y-1.5 text-xs text-slate-500" data-testid="meeting-history">
              {[...(m.history || [])].reverse().map((h, i) => <li key={i}><span className="font-medium text-slate-700">{h.action}</span> · {h.by} · {fmtDate(h.at)}</li>)}
            </ul>
          </Card>
        </div>
      </div>
      <ChooseDocDialog meeting={m} open={choose} onClose={() => setChoose(false)} onDone={() => { setChoose(false); load(); }} />
    </div>
  );
}
